/** @graphql agg **/
import { useCallback, useEffect, useMemo, useState } from 'react';
import { graphql } from 'react-relay';

import { expVal } from '@confluence/feature-experiments';
import { cfetch, BadStatusError } from '@confluence/network';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useFetchSuggestionCountQuery$data,
	useFetchSuggestionCountQuery$variables,
} from './__generated__/useFetchSuggestionCountQuery.graphql.ts';

export const RemoteObjectSuggestionCountQuery = graphql`
	query useFetchSuggestionCountQuery($cloudId: ID!, $spaceKey: String!) {
		confluence_remoteObjectSuggestionCount(cloudId: $cloudId, spaceKey: $spaceKey)
	}
`;

export type UseFetchSuggestionCountResult = {
	count: number;
	isLoading: boolean;
	isSuppressed: boolean;
	refetch: () => void;
};

type UseFetchSuggestionCountProps = {
	spaceKey?: string;
	cloudId: string;
};

export const useFetchSuggestionCount = ({
	spaceKey,
	cloudId,
}: UseFetchSuggestionCountProps): UseFetchSuggestionCountResult => {
	// Apollo path — runs when experiment is on. useQueryInterop must be called
	// unconditionally; the skip option prevents network activity when unused.
	const {
		data: apolloData,
		loading: apolloLoading,
		refetch: apolloRefetch,
	} = useQueryInterop<useFetchSuggestionCountQuery$data, useFetchSuggestionCountQuery$variables>(
		RemoteObjectSuggestionCountQuery,
		{
			variables: { cloudId, spaceKey: spaceKey ?? '' },
			skip:
				!expVal('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', false) ||
				!spaceKey,
			interopApolloAggClient: true,
		},
	);

	const apolloResult = useMemo<UseFetchSuggestionCountResult | null>(() => {
		if (!expVal('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', false)) {
			return null;
		}
		const raw = apolloData?.confluence_remoteObjectSuggestionCount;
		// null data is the suppression signal — this includes the case where the
		// backend returns errors alongside null data (e.g. NotFoundException when
		// the user has been permanently suppressed). Do not gate on apolloError.
		const apolloIsSuppressed = !apolloLoading && raw == null;
		return {
			count: raw ?? 0,
			isLoading: apolloLoading,
			isSuppressed: apolloIsSuppressed,
			refetch: () => {
				void apolloRefetch();
			},
		};
	}, [apolloData, apolloLoading, apolloRefetch]);

	// cfetch path — used when experiment is off
	const [count, setCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isSuppressed, setIsSuppressed] = useState(false);

	const fetchCount = useCallback(async () => {
		if (expVal('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', false)) {
			return;
		}

		if (!spaceKey) {
			setCount(0);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);

		try {
			const response = await cfetch(
				`/wiki/rest/internal/1.0/spaces/${spaceKey}/remote-objects/suggestions/count`,
				{ method: 'GET' },
			);

			const text = await response.text();
			const parsed = parseInt(text, 10);
			if (isNaN(parsed)) {
				setIsSuppressed(true);
			} else {
				setCount(parsed);
				setIsSuppressed(false);
			}
		} catch (error) {
			if (BadStatusError.isBadStatusError(error) && error.response.status === 404) {
				setIsSuppressed(true);
			}
			// silently fail on other errors
		} finally {
			setIsLoading(false);
		}
	}, [spaceKey]);

	useEffect(() => {
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		void fetchCount();
	}, [fetchCount]);

	if (expVal('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', false)) {
		return apolloResult ?? { count, isLoading, isSuppressed, refetch: fetchCount };
	}

	return { count, isLoading, isSuppressed, refetch: fetchCount };
};
